import { Injectable } from '@angular/core';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { UnRx } from '@pebula/utils';
import {
  EnvironmentService,
  MixpanelIdentityProperties,
  PendoUs,
  PendoUsService,
  SgEntityBasedSession,
  SgHeapIoService,
  SgHotjarService,
  SgMixpanelService,
} from '@sg/common/src';
import { CookiesConsentDialogComponent } from './cookies-consent-dialog/cookies-consent-dialog.component';

@Injectable()
@UnRx()
export class TrackingManagerService {
  dialogRef: MatDialogRef<any>;

  constructor(
    public env: EnvironmentService,
    private session: SgEntityBasedSession,
    private dialog: MatDialog,
    private heapService: SgHeapIoService,
    private hotjarService: SgHotjarService,
    private pendoService: PendoUsService,
    private mixpanelService: SgMixpanelService
  ) {}

  initTrackingPlatforms(): void {
    const isStandalonePage = this.env.branding.name === 'standAlone';
    const trackingConsent = this.session.user.userSettings.trackingConsent;
    // Mixpanel is a lender-app-only vendor. TrackingManagerService is shared (the lead app calls
    // initTrackingPlatforms too), so everything Mixpanel must be gated on the lender app — otherwise
    // the re-prompt, the consent toggle and the SDK load would leak into other apps.
    const isLenderApp = this.env.appNameWithoutPrefix === 'lender';

    // Pendo and Mixpanel are vendors added after the original consent flow. A `null` value means
    // this user has never been asked about that vendor, so we must re-prompt (they can then set it
    // to either true or false). Mixpanel consent does not carry over from the others.
    const isUpdatedCookiesConsent =
      trackingConsent?.pendo === null || (isLenderApp && trackingConsent?.mixpanel === null);

    if (!isStandalonePage && (!trackingConsent || isUpdatedCookiesConsent)) {
      this.dialogRef = this.dialog.open(CookiesConsentDialogComponent, {
        minWidth: '100%',
        minHeight: '420px',
        disableClose: true,
        backdropClass: 'sg-backdrop',
        panelClass: 'sg-user-concent-panel',
        closeOnNavigation: false,
        position: { top: 'top' },
        autoFocus: false,
      });
    } else {
      if (trackingConsent.heap) {
        this.heapService.load(this.env.heap.app_id);
      }
      if (trackingConsent.hotjar) {
        this.hotjarService.loadHotjar();
      }
      if (trackingConsent.pendo) {
        this.pendoService.load(this.env.pendo.apiKey_us);
        this.session.currentEntity$.pipe(UnRx(this)).subscribe(({ id: legalEntityId }) => {
          const { id: userId } = this.session.user;
          const userData: PendoUs.PendoUserData = {
            userId,
            legalEntityId,
          };
          this.pendoService.initUser(userData);
        });
      }
      if (isLenderApp && trackingConsent.mixpanel) {
        const initialEntity = this.session.currentEntity;
        const buildProps = (entity: typeof initialEntity): MixpanelIdentityProperties => ({
          legalEntityId: entity?.id ?? '',
          legalEntityName: entity?.displayName ?? '',
          userRole: entity?.userRole ?? '',
          appVersion: this.env.meta?.version ?? '',
        });
        // Identify + register the current entity inside load() (before the buffered events flush)
        // so early events like email_link_opened already carry the super-properties.
        this.mixpanelService.load(
          this.env.mixpanel?.token ?? '',
          this.session.user.id ?? '',
          buildProps(initialEntity)
        );
        // Then only update the super-properties when the legal entity actually changes (currentEntity$
        // can re-emit the same entity on token renew / re-nav, so dedupe on id).
        let lastEntityId = initialEntity?.id;
        this.session.currentEntity$.pipe(UnRx(this)).subscribe((entity) => {
          if (entity.id === lastEntityId) {
            return;
          }
          this.mixpanelService.updateIdentityProperties(buildProps(entity));
          lastEntityId = entity.id;
        });
      }
    }
  }
}
